/**
 * Apple Pay Button Component for v2 Architecture
 * Clean rewrite based on working CMS implementation
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { getBasisTheoryApiKey } from '../../../react/config/payment';
import { getCurrencyInfo, minorUnitsToMajorUnits } from '../../../react/utils/money';
import { CheckoutData } from '../../core/resources/checkout';
import { Address } from '../../core/resources/expressPaymentMethods';
import { useExpressPaymentMethods } from '../hooks/useExpressPaymentMethods';
import { usePaymentQuery } from '../hooks/usePaymentQuery';
import { useShippingRatesQuery } from '../hooks/useShippingRatesQuery';

// Extend Window interface for Apple Pay
declare global {
  interface Window {
    ApplePaySession: typeof ApplePaySession;
  }
}

// Apple Pay Session types (matches CMS implementation)
declare class ApplePaySession {
  static STATUS_SUCCESS: number;
  static STATUS_FAILURE: number;
  static canMakePayments(): boolean;

  constructor(version: number, request: any);

  begin(): void;
  abort(): void;
  completeMerchantValidation(merchantSession: any): void;
  completePayment(status: number): void;
  completeShippingContactSelection(
    status: number,
    shippingMethods: any[],
    total: any,
    lineItems: any[],
  ): void;
  completeShippingMethodSelection(status: number, total: any, lineItems: any[]): void;

  onvalidatemerchant: ((event: any) => void) | null;
  onpaymentauthorized: ((event: any) => void) | null;
  onshippingcontactselected: ((event: any) => void) | null;
  onshippingmethodselected: ((event: any) => void) | null;
  oncancel: (() => void) | null;
  onerror: ((event: any) => void) | null;
}

interface ApplePayPaymentContact {
  phoneNumber?: string;
  emailAddress?: string;
  givenName?: string;
  familyName?: string;
  addressLines?: string[];
  locality?: string;
  administrativeArea?: string;
  postalCode?: string;
  countryCode?: string;
}

export interface ApplePayButtonProps {
  checkout: CheckoutData;
  onSuccess?: (result: { payment: any; order: any }) => void;
  onError?: (error: string) => void;
  onCancel?: () => void;
}

// Helper function to convert Apple Pay contact to Address (matches CMS)
const applePayContactToAddress = (contact: ApplePayPaymentContact): Address => {
  return {
    address1: contact?.addressLines?.[0] || '',
    address2: contact?.addressLines?.[1] || '',
    lastName: contact?.familyName || '',
    firstName: contact?.givenName || '',
    city: contact?.locality || '',
    state: contact?.administrativeArea || '',
    country: contact?.countryCode || '',
    postal: contact?.postalCode || '',
    phone: contact?.phoneNumber || '',
    email: contact?.emailAddress || '',
  };
};

const APPLE_PAY_SDK_URL = 'https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js';
let applePaySdkPromise: Promise<void> | null = null;

function loadApplePaySdk(): Promise<void> {
  if (applePaySdkPromise) return applePaySdkPromise;

  // Check if the script is already in the DOM (e.g. from a previous load)
  if (document.querySelector(`script[src="${APPLE_PAY_SDK_URL}"]`)) {
    applePaySdkPromise = Promise.resolve();
    return applePaySdkPromise;
  }

  applePaySdkPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = APPLE_PAY_SDK_URL;
    script.crossOrigin = 'anonymous';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      applePaySdkPromise = null;
      reject(new Error('[ApplePay] Failed to load Apple Pay SDK'));
    };
    document.head.appendChild(script);
  });

  return applePaySdkPromise;
}

export const ApplePayButton: React.FC<ApplePayButtonProps> = ({ checkout, onSuccess, onError, onCancel }) => {
  const {
    applePayPaymentMethod,
    reComputeOrderSummary,
    shippingMethods,
    lineItems,
    handleAddExpressId,
    updateCheckoutSessionValues,
    updateCustomerEmail,
    setError: setContextError,
  } = useExpressPaymentMethods();

  const [processingPayment, setProcessingPayment] = useState<boolean>(false);
  const [isApplePayAvailable, setIsApplePayAvailable] = useState<boolean>(false);

  // Get Basis Theory API key
  const basistheoryPublicKey = useMemo(() => getBasisTheoryApiKey(), []);

  // Use payment hook for proper payment processing
  const { processApplePayPayment } = usePaymentQuery();

  // Use shipping rates hook for selecting shipping rates
  const { selectRate } = useShippingRatesQuery({ checkout });

  // Don't render if no Apple Pay payment method is enabled
  if (!applePayPaymentMethod) {
    return null;
  }

  // Load SDK on demand, then check Apple Pay availability
  useEffect(() => {
    let cancelled = false;

    const checkAvailability = () => {
      try {
        if (!cancelled && window?.ApplePaySession && ApplePaySession.canMakePayments()) {
          setIsApplePayAvailable(true);
          handleAddExpressId('apple_pay');
        }
      } catch (error) {
        console.warn('[ApplePay] Apple Pay not available:', error);
      }
    };

    loadApplePaySdk()
      .then(checkAvailability)
      .catch(() => {});

    return () => { cancelled = true; };
  }, [handleAddExpressId]);

  // Helper to convert minor units to currency string
  const minorUnitsToCurrencyString = useCallback(
    (amountMinor: number | undefined, currency: string | undefined) => {
      // Fail safely - don't allow invalid data to become '0.00'
      if (amountMinor === undefined || amountMinor === null || !currency) {
        throw new Error(`Invalid currency data: amountMinor=${amountMinor}, currency=${currency}`);
      }

      const currencyInfo = getCurrencyInfo(currency);
      if (!currencyInfo) {
        console.warn(`Currency info not found for ${currency}, using fallback`);
        return (amountMinor / 100).toFixed(2);
      }

      // 0 is a valid amount (e.g., free items)
      const majorUnits = minorUnitsToMajorUnits(amountMinor, currency);
      return majorUnits.toFixed(currencyInfo.ISOdigits);
    },
    [],
  );

  // Validate merchant with Basis Theory (matches CMS)
  const validateMerchant = useCallback(async (): Promise<any> => {
    try {
      const response = await fetch('https://api.basistheory.com/apple-pay/session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'BT-API-KEY': basistheoryPublicKey,
        },
        body: JSON.stringify({
          display_name: checkout.checkoutSession.store?.name || 'Store',
          domain: window.location.host,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const merchantSession = await response.json();
      return merchantSession;
    } catch (err) {
      console.error('[ApplePay] Merchant validation failed:', err);
      throw err;
    }
  }, [basistheoryPublicKey, checkout.checkoutSession.store?.name]);

  // Tokenize Apple Pay payment with Basis Theory (matches CMS)
  const tokenizeApplePay = useCallback(
    async (event: any): Promise<any> => {
      try {
        const response = await fetch('https://api.basistheory.com/apple-pay', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'BT-API-KEY': basistheoryPublicKey,
          },
          body: JSON.stringify({
            apple_payment_data: event.payment.token,
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const result = await response.json();
        return result.apple_pay; // Basis Theory returns the Apple Pay token in the apple_pay field
      } catch (error) {
        console.error('[ApplePay] Tokenizing Apple Pay failed:', error);
        throw error;
      }
    },
    [basistheoryPublicKey],
  );

  // Process payment using the hook (handles order creation, instrument creation, payment processing, and polling)
  const handleApplePayPayment = useCallback(
    async (payToken: any): Promise<any> => {
      setProcessingPayment(true);

      try {
        // Use the hook's method which handles everything properly
        const result = await processApplePayPayment(checkout.checkoutSession.id, payToken, {
          // Callbacks are handled by the hook, but we can intercept for custom behavior
          onPaymentSuccess: (response) => {
            // Keep processingPayment true for loading state during navigation
            // The hook already returns the response, we just need to return it
          },
          onPaymentFailed: (error) => {
            console.error('[ApplePay] Payment failed:', error);
            setProcessingPayment(false);
            throw new Error(error.message);
          },
        });

        // Return the result (contains payment and order)
        return result;
      } catch (error) {
        console.error('[ApplePay] Payment processing failed:', error);
        setProcessingPayment(false);
        throw error;
      }
    },
    [processApplePayPayment, checkout.checkoutSession.id],
  );

  // Handle Apple Pay button click (matches CMS implementation)
  const handleApplePayClick = useCallback(() => {
    if (!isApplePayAvailable || processingPayment || !checkout.summary) {
      return;
    }

    const storeName = checkout.checkoutSession.store?.name || 'Store';

    const total = {
      label: storeName,
      amount: minorUnitsToCurrencyString(checkout.summary.totalAdjustedAmount, checkout.summary.currency),
      type: 'final',
    };

    const request: any = {
      countryCode: (applePayPaymentMethod?.metadata?.country as string) || 'US',
      currencyCode: checkout.summary.currency,
      supportedNetworks: ['visa', 'masterCard', 'amex', 'discover'],
      merchantCapabilities: ['supports3DS'],
      total,
      shippingMethods,
      lineItems,
      requiredShippingContactFields: ['name', 'phone', 'email', 'postalAddress'],
      requiredBillingContactFields: ['postalAddress'],
    };

    try {
      const session = new ApplePaySession(3, request);

      // Merchant validation (matches CMS)
      session.onvalidatemerchant = (event: any) => {
        void (async () => {
          try {
            const merchantSession = await validateMerchant();
            session.completeMerchantValidation(merchantSession);
          } catch (error) {
            console.error('[ApplePay] Merchant validation failed:', error);
            session.abort();
          }
        })();
      };

      // Payment authorized (matches CMS)
      session.onpaymentauthorized = (event: any) => {
        void (async () => {
          try {
            const shippingContact = event.payment.shippingContact;
            const billingContact = event.payment.billingContact;
            const shippingAddress = applePayContactToAddress(shippingContact);
            const billingAddress = applePayContactToAddress(billingContact);

            await updateCheckoutSessionValues({
              data: {
                shippingAddress,
                billingAddress,
              },
            });

            if (shippingContact.emailAddress) {
              await updateCustomerEmail({
                data: {
                  email: shippingContact.emailAddress,
                },
              });
            }

            const applePayToken = await tokenizeApplePay(event);
            session.completePayment(ApplePaySession.STATUS_SUCCESS);

            const result = await handleApplePayPayment(applePayToken);

            // Call success callback (triggers navigation)
            // Note: processingPayment stays true until component unmounts during navigation
            if (onSuccess) {
              onSuccess(result);
            }
          } catch (error) {
            console.error('[ApplePay] Payment processing failed:', error);
            session.completePayment(ApplePaySession.STATUS_FAILURE);
            const errorMessage = 'Payment Failed';
            setContextError(errorMessage);
            if (onError) {
              onError(errorMessage);
            }
          }
        })();
      };

      // Shipping method selected (matches CMS - abort if recompute fails)
      session.onshippingmethodselected = (event: any) => {
        void (async () => {
          try {
            // Update shipping method via client
            await selectRate(event.shippingMethod.identifier);

            const newOrderSummary = await reComputeOrderSummary();
            if (!newOrderSummary) {
              console.error('[ApplePay] No order summary returned, aborting session');
              session.abort();
              return;
            }

            const { lineItems: newLineItems, total: newTotal } = newOrderSummary;

            // Ensure type: 'final' is present for Apple Pay (matches CMS)
            const formattedTotal = {
              ...newTotal,
              type: 'final' as const,
            };
            const formattedLineItems = newLineItems.map((item) => ({
              ...item,
              type: 'final' as const,
            }));

            session.completeShippingMethodSelection(
              ApplePaySession.STATUS_SUCCESS,
              formattedTotal,
              formattedLineItems,
            );
          } catch (error) {
            console.error('[ApplePay] Shipping method selection failed:', error);
            session.abort();
          }
        })();
      };

      // Shipping contact selected (matches CMS - abort if recompute fails)
      session.onshippingcontactselected = (event: any) => {
        void (async () => {
          const shippingContact = event.shippingContact;
          try {
            await updateCheckoutSessionValues({
              data: {
                shippingAddress: applePayContactToAddress(shippingContact),
              },
            });

            const newOrderSummary = await reComputeOrderSummary();
            if (!newOrderSummary) {
              console.error('[ApplePay] No order summary returned, aborting session');
              session.abort();
              setContextError('Payment Failed');
              return;
            }

            const {
              lineItems: newLineItems,
              total: newTotal,
              shippingMethods: newShippingMethods,
            } = newOrderSummary;

            // Ensure type: 'final' is present for Apple Pay (matches CMS)
            const formattedTotal = {
              ...newTotal,
              type: 'final' as const,
            };
            const formattedLineItems = newLineItems.map((item) => ({
              ...item,
              type: 'final' as const,
            }));

            session.completeShippingContactSelection(
              ApplePaySession.STATUS_SUCCESS,
              newShippingMethods,
              formattedTotal,
              formattedLineItems,
            );
          } catch (error) {
            console.error('[ApplePay] Shipping contact selection failed:', error);
            session.abort();
          }
        })();
      };

      session.onerror = (event: any) => {
        console.error('[ApplePay] Session Error:', event);
      };

      session.oncancel = () => {
        if (onCancel) {
          onCancel();
        }
      };

      session.begin();
    } catch (error) {
      console.error('[ApplePay] Failed to start Apple Pay session:', error);
    }
  }, [
    isApplePayAvailable,
    processingPayment,
    checkout,
    shippingMethods,
    lineItems,
    applePayPaymentMethod,
    minorUnitsToCurrencyString,
    validateMerchant,
    tokenizeApplePay,
    processApplePayPayment,
    updateCheckoutSessionValues,
    updateCustomerEmail,
    reComputeOrderSummary,
    setContextError,
    selectRate,
    onSuccess,
    onError,
    onCancel,
  ]);

  if (!isApplePayAvailable) {
    return null;
  }

  return (
    <button
      onClick={handleApplePayClick}
      disabled={processingPayment}
      style={{
        backgroundColor: '#000',
        color: '#fff',
        border: 'none',
        borderRadius: '4px',
        padding: '12px',
        width: '100%',
        fontSize: '16px',
        fontWeight: '500',
        cursor: processingPayment ? 'not-allowed' : 'pointer',
        opacity: processingPayment ? 0.6 : 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
      }}
      type="button"
    >
      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
      </svg>
      {processingPayment ? 'Processing...' : 'Pay with Apple Pay'}
    </button>
  );
};

export default ApplePayButton;
